import { useCallback, useMemo } from 'react';

import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import {
  IClaimApprovalStatusAggregatorDtoClaimApprovalStatusCodeEnum as ClaimApprovalStatusCode,
  ICreateClaimAggregatorDtoPaymentTypeEnum,
  ICreateClaimAggregatorDtoPayToTypeEnum,
} from '@/dtos';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { CreateClaimDto, CreatedClaimDto, CreatedHsaWithdrawalClaimDto, CreateHsaWithdrawalClaimDto } from '@/modules/UseMyMoney/queries';
import { useMyMoneyFormStore, useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import {
  CLAIM_AUTOMATICALLY_APPROVAL_STATUS_CODES,
  CLAIM_CREATED_WITHOUT_DOCUMENT_STATUS_CODES,
  USE_MY_MONEY_UPLOAD_STORE_KEY,
  UseMyMoneyOption,
} from '@/modules/UseMyMoney/UseMyMoney.types';

const checkClaimApprovalStatusCode = (checkCodes: ClaimApprovalStatusCode[], codes?: ClaimApprovalStatusCode[]) => codes
  ?.some((code) => checkCodes.includes(code));

interface Props {
  createClaim: (claimDto: CreateClaimDto) => Promise<CreatedClaimDto>;
  createHsaWithdrawalClaim: (claimDto: CreateHsaWithdrawalClaimDto) => Promise<CreatedHsaWithdrawalClaimDto>;
  onCreateClaim: (createdClaimId?: string) => void;
}

const useSubmitClaim = ({ createClaim, onCreateClaim, createHsaWithdrawalClaim }: Props) => {
  const { useMyMoneyOption } = useMyMoneyStore();
  const {
    values: formValues,
    handleSetState,
    isAccountNumberEnabled,
    isInvoiceNumberEnabled,
  } = useMyMoneyFormStore();
  const activeMethod = useStore((state) => state.activeMethod);
  const { values: documentValues } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);

  const createClaimDto = useMemo((): CreateClaimDto | CreateHsaWithdrawalClaimDto | undefined => {
    if (useMyMoneyOption === UseMyMoneyOption.REIMBURSE_MYSELF) {
      return {
        amount: formValues?.amount,
        providerId: formValues?.providerId,
        patientId: formValues?.patientId,
        dateOfService: formValues?.dateOfService,
        serviceId: formValues?.serviceId,
        notes: formValues?.notes,
        lmnId: formValues?.lmnId,
        paymentMethodId: activeMethod?.id,
        paymentType: ICreateClaimAggregatorDtoPaymentTypeEnum.REIMBURSEMENT,
        payToType: ICreateClaimAggregatorDtoPayToTypeEnum.PARTICIPANT,
        documentId: documentValues?.documentId,
      };
    }

    if (useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER) {
      return {
        amount: formValues?.amount,
        providerId: formValues?.providerId,
        patientId: formValues?.patientId,
        dateOfService: formValues?.dateOfService,
        serviceId: formValues?.serviceId,
        notes: formValues?.notes,
        lmnId: formValues?.lmnId,
        accountNumber: isAccountNumberEnabled ? formValues?.accountNumber : undefined,
        invoiceNumber: isInvoiceNumberEnabled ? formValues?.invoiceNumber : undefined,
        paymentMethodId: formValues?.paymentMethodId,
        paymentType: ICreateClaimAggregatorDtoPaymentTypeEnum.BILL_PAY,
        payToType: ICreateClaimAggregatorDtoPayToTypeEnum.PROVIDER,
        documentId: documentValues?.documentId,
      };
    }

    if (useMyMoneyOption === UseMyMoneyOption.HSA_WITHDRAWAL) {
      return {
        amount: formValues?.amount,
        accountId: formValues?.accountId,
        notes: formValues?.notes,
        paymentMethodId: activeMethod?.id,
        documentId: documentValues?.documentId,
      };
    }
  }, [useMyMoneyOption, formValues, activeMethod, documentValues, isAccountNumberEnabled, isInvoiceNumberEnabled]);

  const handleSubmitClaim = useCallback(async () => {
    if (!createClaimDto) return;

    try {
      const response = await createClaim(createClaimDto);

      const isAutomaticallyApproved = response?.claim_approval_status_dtos?.some((status) =>
        checkClaimApprovalStatusCode(
          CLAIM_AUTOMATICALLY_APPROVAL_STATUS_CODES,
          status?.claim_approval_status_codes as unknown as ClaimApprovalStatusCode[]
        )
      );

      const isCreatedWithoutDocument = response?.claim_approval_status_dtos?.some((status) =>
        checkClaimApprovalStatusCode(
          CLAIM_CREATED_WITHOUT_DOCUMENT_STATUS_CODES,
          status?.claim_approval_status_codes as unknown as ClaimApprovalStatusCode[]
        )
      );

      const createdClaimId = response?.id ? `${response?.id}` : undefined;
      onCreateClaim(createdClaimId);
      handleSetState({
        showUnsavedChangesPrompt: false,
        isClaimAutomaticallyApproved: !!isAutomaticallyApproved,
        isClaimCreatedWithoutDocument: !!isCreatedWithoutDocument,
      });
    } catch (error) {
      console.log(error);
    }
  }, [createClaim, onCreateClaim, handleSetState, createClaimDto]);

  const handleSubmitHsaWithdrawal = useCallback(async () => {
    if (!createClaimDto) return;

    try {
      const response = await createHsaWithdrawalClaim(createClaimDto);
      const createdClaimId = response?.id ? `${response?.id}` : undefined;
      onCreateClaim(createdClaimId);
      handleSetState({ showUnsavedChangesPrompt: false });
    } catch (error) {
      console.log(error);
    }
  }, [createClaimDto, createHsaWithdrawalClaim, handleSetState, onCreateClaim]);

  const handleSubmit = useMemo(() => {
    if (useMyMoneyOption === UseMyMoneyOption.REIMBURSE_MYSELF) {
      return handleSubmitClaim;
    }
    if (useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER) {
      return handleSubmitClaim;
    }
    if (useMyMoneyOption === UseMyMoneyOption.HSA_WITHDRAWAL) {
      return handleSubmitHsaWithdrawal;
    }
  }, [useMyMoneyOption, handleSubmitClaim, handleSubmitHsaWithdrawal]);

  return {
    handleSubmit,
    createClaimDto,
    useMyMoneyOption,
  };
};

export default useSubmitClaim;
